import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  CssBaseline,
  Divider,
  Grid,
  IconButton,
  Slider,
  Stack,
  ThemeProvider,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AdminPanelSettings,
  ArrowForward,
  Build,
  Campaign,
  EventAvailable,
  Groups,
  Handshake,
  InfoOutlined,
  Inventory,
  LocalShipping,
  Payment,
  Person,
  Receipt,
  ShieldOutlined,
  ShoppingCart,
  TrendingDown,
  TrendingUp,
  Warehouse,
  AccountBalanceWallet,
  Insights,
  Hub,
  PhoneInTalk,
} from '@mui/icons-material';
import LeadCaptureModal from './LeadCaptureModal';
import { BrandMark, WealthGraphic } from '../customer/portal/PortalUi';
import { brand, portalTheme } from '../customer/portal/portalTheme';
import { ReleasepilotAffiliation } from '../../shared-components/branding/ReleasepilotAffiliation';
import { LanguageSwitcher, useTranslation } from '../../i18n';

const sections = [
  { id: 'how-it-works', label: 'howItWorks' },
  { id: 'calculator', label: 'calculator' },
  { id: 'services', label: 'services' },
  { id: 'disclaimer', label: 'disclaimer' },
] as const;

const referralSteps = [
  { id: 'findBuddy', icon: <Groups /> },
  { id: 'inherit', icon: <Handshake /> },
  { id: 'reputation', icon: <ShieldOutlined /> },
  { id: 'grow', icon: <TrendingUp /> },
] as const;

const repaymentTerms = [
  { id: 'principal', icon: <AccountBalanceWallet /> },
  { id: 'earlyClosure', icon: <EventAvailable /> },
  { id: 'methods', icon: <Payment /> },
] as const;

const useCases = [
  { id: 'salary', icon: <Payment fontSize="small" /> },
  { id: 'rawMaterials', icon: <ShoppingCart fontSize="small" /> },
  { id: 'workingCapital', icon: <TrendingDown fontSize="small" /> },
  { id: 'inventory', icon: <Inventory fontSize="small" /> },
  { id: 'equipment', icon: <Build fontSize="small" /> },
  { id: 'vendors', icon: <LocalShipping fontSize="small" /> },
  { id: 'tax', icon: <Receipt fontSize="small" /> },
  { id: 'campaigns', icon: <Campaign fontSize="small" /> },
  { id: 'offSeason', icon: <Warehouse fontSize="small" /> },
] as const;

const services = [
  { id: 'consultancy', icon: <Insights /> },
  { id: 'saas', icon: <Hub /> },
] as const;

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);

const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

function SectionHeading({ kicker, title, subtitle }: { kicker: string; title: string; subtitle?: string }) {
  return (
    <Box sx={{ mb: 4, maxWidth: 720 }}>
      <Typography variant="overline" sx={{ color: brand.gold, fontWeight: 700, letterSpacing: 1.2 }}>
        {kicker}
      </Typography>
      <Typography variant="h4" component="h2" sx={{ fontWeight: 700, color: brand.navy, fontSize: { xs: '1.6rem', md: '2rem' } }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography sx={{ color: brand.slate, mt: 1 }}>{subtitle}</Typography>
      ) : null}
    </Box>
  );
}

function IconTile({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 2,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: brand.navy,
        bgcolor: brand.goldSoft,
        flexShrink: 0,
      }}
    >
      {children}
    </Box>
  );
}

const sliderSx = {
  color: brand.gold,
  '& .MuiSlider-thumb': { bgcolor: '#fff', border: `2px solid ${brand.gold}` },
  '& .MuiSlider-markLabel': { color: brand.slate, fontSize: '0.75rem' },
};

const HomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t, tList, language } = useTranslation();

  const [loanAmount, setLoanAmount] = React.useState(500000);
  const [interestRate, setInterestRate] = React.useState(2);
  const [tenureMonths, setTenureMonths] = React.useState(12);
  const [leadModalOpen, setLeadModalOpen] = React.useState(false);

  // Monthly interest = principal × (monthly rate / 100); total = principal + months × monthly interest
  const monthlyInterest = loanAmount * (interestRate / 100);
  const totalAmount = loanAmount + tenureMonths * monthlyInterest;
  const totalInterest = totalAmount - loanAmount;

  const sectionSx = { py: { xs: 6, md: 8 }, scrollMarginTop: 72 };

  return (
    <ThemeProvider theme={portalTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: brand.ivory, color: brand.navy }}>
        <AppBar
          position="sticky"
          elevation={0}
          sx={{ bgcolor: brand.navy, borderBottom: '1px solid rgba(255,255,255,0.08)' }}
        >
          <Container maxWidth="lg">
            <Toolbar disableGutters sx={{ gap: 2, minHeight: 64 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1.25, cursor: 'pointer' }}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                <BrandMark size={34} />
                <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#fff' }}>Whatzneu</Typography>
              </Box>
              <Stack direction="row" spacing={0.5} sx={{ ml: 3, display: { xs: 'none', lg: 'flex' } }}>
                {sections.map((section) => (
                  <Button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 500, '&:hover': { color: '#fff' } }}
                  >
                    {t(`home.nav.${section.label}`)}
                  </Button>
                ))}
              </Stack>
              <Box sx={{ flex: 1 }} />
              <LanguageSwitcher />
              <Button
                variant="contained"
                size="small"
                startIcon={<Person />}
                onClick={() => navigate('/customer/login')}
                sx={{ bgcolor: brand.gold, color: brand.navy, fontWeight: 700, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#D9C08A' } }}
              >
                {t('home.nav.customerLogin')}
              </Button>
              <Tooltip title={t('home.nav.adminLogin')}>
                <IconButton
                  aria-label={t('home.nav.adminLogin')}
                  onClick={() => navigate('/login')}
                  size="small"
                  sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}
                >
                  <AdminPanelSettings fontSize="small" />
                </IconButton>
              </Tooltip>
            </Toolbar>
          </Container>
        </AppBar>

        <Box sx={{ bgcolor: brand.navy, color: '#fff', pb: { xs: 6, md: 8 }, pt: { xs: 5, md: 7 } }}>
          <Container maxWidth="lg">
            <Grid container spacing={5} alignItems="center">
              <Grid item xs={12} md={7}>
                <Chip
                  icon={<Handshake sx={{ color: `${brand.navy} !important` }} />}
                  label={t('home.hero.badge')}
                  size="small"
                  sx={{ bgcolor: brand.gold, color: brand.navy, fontWeight: 700, mb: 2.5 }}
                />
                <Typography
                  variant="h1"
                  sx={{ fontWeight: 700, fontSize: { xs: '2.1rem', md: '3rem' }, lineHeight: 1.15, mb: 2 }}
                >
                  {t('home.hero.title')}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.78)', fontSize: { xs: '1rem', md: '1.125rem' }, mb: 3.5, maxWidth: 560 }}>
                  {t('home.hero.subtitle')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 3 }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<PhoneInTalk />}
                    onClick={() => setLeadModalOpen(true)}
                    sx={{ bgcolor: brand.gold, color: brand.navy, fontWeight: 700, px: 3, '&:hover': { bgcolor: '#D9C08A' } }}
                  >
                    {t('home.hero.requestCallBack')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    endIcon={<ArrowForward />}
                    onClick={() => scrollToSection('how-it-works')}
                    sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.35)', px: 3, '&:hover': { borderColor: '#fff' } }}
                  >
                    {t('home.hero.howItWorks')}
                  </Button>
                </Stack>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-start',
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid rgba(198,167,106,0.4)',
                    bgcolor: 'rgba(198,167,106,0.08)',
                    maxWidth: 560,
                  }}
                >
                  <InfoOutlined sx={{ color: brand.gold, fontSize: 18, mt: '2px' }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    {t('home.hero.notice')}{' '}
                    <Box
                      component="button"
                      type="button"
                      onClick={() => scrollToSection('disclaimer')}
                      sx={{
                        p: 0,
                        border: 0,
                        background: 'none',
                        color: brand.gold,
                        font: 'inherit',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                    >
                      {t('home.hero.readDisclaimer')}
                    </Box>
                  </Typography>
                </Box>
                <Box sx={{ mt: 2.5 }}>
                  <ReleasepilotAffiliation variant="onDark" />
                </Box>
              </Grid>
              <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
                <WealthGraphic size={340} />
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Box id="how-it-works" component="section" sx={sectionSx}>
          <Container maxWidth="lg">
            <SectionHeading
              kicker={t('home.howItWorks.kicker')}
              title={t('home.howItWorks.title')}
              subtitle={t('home.howItWorks.subtitle')}
            />
            <Grid container spacing={2.5}>
              {referralSteps.map((step, index) => (
                <Grid item xs={12} sm={6} md={3} key={step.id}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                        <IconTile>{step.icon}</IconTile>
                        <Typography variant="caption" sx={{ color: brand.slate, fontWeight: 700 }}>
                          {t('home.howItWorks.stepLabel', { number: index + 1 })}
                        </Typography>
                      </Stack>
                      <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{t(`home.howItWorks.steps.${step.id}.title`)}</Typography>
                      <Typography variant="body2" sx={{ color: brand.slate }}>
                        {t(`home.howItWorks.steps.${step.id}.description`)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={2.5} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography sx={{ fontWeight: 700, mb: 2 }}>{t('home.howItWorks.repaymentTitle')}</Typography>
                    <Stack spacing={1.5}>
                      {repaymentTerms.map((term) => (
                        <Stack direction="row" spacing={1.5} alignItems="center" key={term.id}>
                          <IconTile>{term.icon}</IconTile>
                          <Typography variant="body2">{t(`home.howItWorks.repayment.${term.id}`)}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography sx={{ fontWeight: 700, mb: 2 }}>{t('home.howItWorks.useCasesTitle')}</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {useCases.map((useCase) => (
                        <Chip
                          key={useCase.id}
                          icon={useCase.icon}
                          label={t(`home.howItWorks.useCases.${useCase.id}`)}
                          variant="outlined"
                          sx={{ borderColor: brand.border, color: brand.charcoal, '& .MuiChip-icon': { color: brand.gold } }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Box id="calculator" component="section" sx={{ ...sectionSx, bgcolor: '#fff', borderTop: `1px solid ${brand.border}`, borderBottom: `1px solid ${brand.border}` }}>
          <Container maxWidth="lg">
            <SectionHeading
              kicker={t('home.calculator.kicker')}
              title={t('home.calculator.title')}
              subtitle={t('home.calculator.subtitle')}
            />
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <Card>
                  <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                    <Stack spacing={3.5}>
                      <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                          <Typography sx={{ fontWeight: 600 }}>{t('home.calculator.amount')}</Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: '1.15rem' }}>{formatCurrency(loanAmount)}</Typography>
                        </Stack>
                        <Slider
                          value={loanAmount}
                          onChange={(_, value) => setLoanAmount(value as number)}
                          min={100000}
                          max={1000000}
                          step={50000}
                          marks={[
                            { value: 100000, label: '₹1L' },
                            { value: 500000, label: '₹5L' },
                            { value: 1000000, label: '₹10L' },
                          ]}
                          aria-label={t('home.calculator.amount')}
                          sx={sliderSx}
                        />
                      </Box>
                      <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                          <Typography sx={{ fontWeight: 600 }}>{t('home.calculator.rate')}</Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: '1.15rem' }}>{t('home.calculator.ratePerMonth', { value: interestRate })}</Typography>
                        </Stack>
                        <Slider
                          value={interestRate}
                          onChange={(_, value) => setInterestRate(value as number)}
                          min={1}
                          max={5}
                          step={0.25}
                          marks={[
                            { value: 1, label: '1%' },
                            { value: 3, label: '3%' },
                            { value: 5, label: '5%' },
                          ]}
                          aria-label={t('home.calculator.rate')}
                          sx={sliderSx}
                        />
                      </Box>
                      <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                          <Typography sx={{ fontWeight: 600 }}>{t('home.calculator.tenure')}</Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: '1.15rem' }}>{t('home.calculator.tenureMonths', { count: tenureMonths })}</Typography>
                        </Stack>
                        <Slider
                          value={tenureMonths}
                          onChange={(_, value) => setTenureMonths(value as number)}
                          min={3}
                          max={36}
                          step={3}
                          marks={[
                            { value: 3, label: t('home.calculator.monthsShort', { count: 3 }) },
                            { value: 12, label: t('home.calculator.monthsShort', { count: 12 }) },
                            { value: 24, label: t('home.calculator.monthsShort', { count: 24 }) },
                            { value: 36, label: t('home.calculator.monthsShort', { count: 36 }) },
                          ]}
                          aria-label={t('home.calculator.tenure')}
                          sx={sliderSx}
                        />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={5}>
                <Card sx={{ height: '100%', bgcolor: brand.navy, color: '#fff', border: 'none' }}>
                  <CardContent sx={{ p: { xs: 2.5, md: 3.5 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      {t('home.calculator.monthlyInterest')}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '2.25rem', color: brand.gold, mb: 2 }}>
                      {formatCurrency(monthlyInterest)}
                    </Typography>
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', mb: 2 }} />
                    <Stack spacing={1.25} sx={{ mb: 3 }}>
                      {[
                        { label: t('home.calculator.principal'), value: loanAmount },
                        { label: t('home.calculator.interestOver', { count: tenureMonths }), value: totalInterest },
                        { label: t('home.calculator.totalPayable'), value: totalAmount },
                      ].map((row) => (
                        <Stack direction="row" justifyContent="space-between" key={row.label}>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            {row.label}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {formatCurrency(row.value)}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                    <Box sx={{ flex: 1 }} />
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => setLeadModalOpen(true)}
                      sx={{ bgcolor: brand.gold, color: brand.navy, fontWeight: 700, '&:hover': { bgcolor: '#D9C08A' } }}
                    >
                      {t('home.calculator.cta')}
                    </Button>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', mt: 1.5 }}>
                      {t('home.calculator.note')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Box id="services" component="section" sx={sectionSx}>
          <Container maxWidth="lg">
            <SectionHeading
              kicker={t('home.services.kicker')}
              title={t('home.services.title')}
              subtitle={t('home.services.subtitle')}
            />
            <Grid container spacing={2.5}>
              {services.map((service) => (
                <Grid item xs={12} md={6} key={service.id}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3, display: 'flex', gap: 2 }}>
                      <IconTile>{service.icon}</IconTile>
                      <Box>
                        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{t(`home.services.${service.id}.title`)}</Typography>
                        <Typography variant="body2" sx={{ color: brand.slate }}>
                          {t(`home.services.${service.id}.description`)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Button endIcon={<ArrowForward />} onClick={() => navigate('/services')} sx={{ mt: 2, color: brand.navy, fontWeight: 600 }}>
              {t('home.services.exploreAll')}
            </Button>
          </Container>
        </Box>

        <Box id="disclaimer" component="section" sx={{ ...sectionSx, pt: 0 }}>
          <Container maxWidth="lg">
            <Alert
              severity="warning"
              icon={<InfoOutlined />}
              sx={{
                bgcolor: '#FBF6EC',
                border: `1px solid ${brand.gold}`,
                color: brand.charcoal,
                borderRadius: 3,
                p: { xs: 2, md: 3 },
                '& .MuiAlert-icon': { color: brand.warning },
              }}
            >
              <Typography component="h2" sx={{ fontWeight: 700, fontSize: '1.15rem', mb: 1.5, color: brand.navy }}>
                {t('home.disclaimer.title')}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'grid', gap: 1 }}>
                {tList('home.disclaimer.points').map(point => (
                  <Typography component="li" variant="body2" key={point}>
                    {point}
                  </Typography>
                ))}
              </Box>
              {language !== 'en' ? (
                <Typography variant="body2" sx={{ mt: 1.5, fontStyle: 'italic' }}>
                  {t('home.disclaimer.englishPrevails')}
                </Typography>
              ) : null}
            </Alert>
          </Container>
        </Box>

        <Box component="footer" sx={{ bgcolor: brand.navy, color: 'rgba(255,255,255,0.7)', py: 4 }}>
          <Container maxWidth="lg">
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <BrandMark size={28} />
                <Typography sx={{ color: '#fff', fontWeight: 700 }}>Whatzneu</Typography>
              </Stack>
              <ReleasepilotAffiliation variant="onDark" />
              <Typography variant="caption">{t('home.footer.rights', { year: new Date().getFullYear() })}</Typography>
            </Stack>
            <Typography variant="caption" component="p" sx={{ mt: 2, color: 'rgba(255,255,255,0.5)' }}>
              {t('home.footer.notice')}
            </Typography>
          </Container>
        </Box>

        <LeadCaptureModal
          open={leadModalOpen}
          onClose={() => setLeadModalOpen(false)}
          initialLoanAmount={loanAmount}
          initialInterestRate={interestRate}
          initialTenureMonths={tenureMonths}
        />
      </Box>
    </ThemeProvider>
  );
};

export default HomeScreen;
